import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatNumber, formatDate, formatCurrency } from '../utils/numberFormatter';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useNavigate } from 'react-router-dom';
import '../styles/bitacora.css';
import '../styles/sales.css';
import '../styles/saleStatusBadges.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import ObservationSaleDetailModal from '../components/Observations/ObservationSaleDetailModal';
import SaleDetailModal from '../components/Sales/SaleDetailModal';
import { TableCard } from '../components/ui';
import { getSaleDisplayStatusLabel, getSaleDisplayStatusClass } from '../utils/saleStatusAdapter';

const MySwal = withReactContent(Swal);

const ObservationPage = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [saleDetailToShow, setSaleDetailToShow] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 25,
    total: 0,
    last_page: 1
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    from_date: '',
    to_date: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    status: '',
    from_date: '',
    to_date: ''
  });

  // Estados permitidos de observación (solo Pendiente y En Proceso)
  const observationStatuses = [
    { value: 'pending', label: 'Pendiente' },
    { value: 'in_progress', label: 'En Proceso' }
  ];

  useEffect(() => {
    fetchObservations();
  }, [appliedFilters, pagination.current_page]);

  const fetchObservations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: appliedFilters.search,
        status: appliedFilters.status,
        date_from: appliedFilters.from_date,
        date_to: appliedFilters.to_date,
        page: pagination.current_page,
        per_page: pagination.per_page
      });

      const response = await api.get(`/observations?${params}`);
      setSales(response.data.observations || []);
      setPagination(response.data.pagination || {
        current_page: 1,
        per_page: 25,
        total: 0,
        last_page: 1
      });
    } catch (error) {
      console.error('Error al obtener observaciones:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron cargar las observaciones',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      search: '',
      status: '',
      from_date: '',
      to_date: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleGoToCustomer = (dni) => {
    navigate(`/clientes/${dni}`);
  };

  const handleViewDetail = (sale) => {
    setSelectedSale(sale);
    setShowDetailModal(true);
  };

  // Componente Skeleton para la tabla
  const TableSkeleton = () => (
    <>
      {Array.from({ length: pagination.per_page }).map((_, index) => (
        <tr key={index}>
          <td><Skeleton width={40} height={30} /></td>
          <td><Skeleton width={80} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td><Skeleton width={60} height={30} /></td>
          <td className="text-center">
            <Skeleton width={50} height={25} />
          </td>
          <td>
            <div className="d-flex gap-2">
              <Skeleton width={30} height={30} />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  // Componente de Paginación
  const Pagination = ({ loading }) => (
    <nav aria-label="Navegación de páginas">
      <ul className="pagination justify-content-center mb-0">
        <li className={`page-item ${loading || pagination.current_page === 1 ? 'disabled' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
            disabled={loading}
          >
            Anterior
          </button>
        </li>
        {Array.from({ length: pagination.last_page }, (_, i) => (
          <li key={i} className={`page-item ${pagination.current_page === i + 1 ? 'active' : ''} ${loading ? 'disabled' : ''}`}>
            <button 
              className="page-link"
              onClick={() => setPagination(prev => ({ ...prev, current_page: i + 1 }))}
              disabled={loading}
            >
              {i + 1}
            </button>
          </li>
        ))}
        <li className={`page-item ${loading || pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
          <button 
            className="page-link"
            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
            disabled={loading}
          >
            Siguiente
          </button>
        </li>
      </ul>
    </nav>
  );

  return (
    <div className="page-content">
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3>Ventas Observadas</h3>
            <p className="text-subtitle text-muted">Ventas observadas pendientes de corrección o validación.</p>
          </div>
        </div>
      </div>

      <section className="section">
        <TableCard
          toolbar={
            <div className="row">
              <div className="col-md-3 mb-2">
                <label htmlFor="search" className="form-label">Buscar</label>
                <input
                  type="text"
                  id="search"
                  className="form-control"
                  placeholder="Cliente o DNI..."
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-3 mb-2">
                <label htmlFor="from_date" className="form-label">Fecha desde</label>
                <input
                  type="date"
                  id="from_date"
                  className="form-control"
                  name="from_date"
                  value={filters.from_date}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-3 mb-2">
                <label htmlFor="to_date" className="form-label">Fecha hasta</label>
                <input
                  type="date"
                  id="to_date"
                  className="form-control"
                  name="to_date"
                  value={filters.to_date}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-md-3 mb-2 d-flex align-items-end">
                <div className="w-100 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-light-primary"
                    onClick={handleApplyFilters}
                  >
                    <i className="bi bi-funnel me-1"></i>
                    Filtrar
                  </button>
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    onClick={handleClearFilters}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Limpiar
                  </button>
                </div>
              </div>
            </div>
          }
          table={
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Venta</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Vendedor</th>
                  <th>Monto</th>
                  <th>Cuotas</th>
                  <th className="text-center">Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton />
                ) : sales.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-5">
                      <i className="bi bi-inbox rg-empty-icon-lg"></i>
                      <p className="mt-3 mb-0">No hay observaciones para mostrar</p>
                    </td>
                  </tr>
                ) : (
                  sales.map((sale) => (
                    <tr key={sale.id}>
                      <td>#{sale.id}</td>
                      <td>
                        <button
                          type="button"
                          onClick={(e) => {
                            handleGoToCustomer(sale.customer?.dni);
                          }}
                          className="rg-link-button"
                        >
                          {sale.customer?.name || 'N/A'}
                        </button>
                        <br />
                        <small className="text-muted">
                          DNI: {sale.customer?.dni || 'N/A'}
                        </small>
                      </td>
                      <td>{sale.dates?.sale || formatDate(sale.dates?.created)}</td>
                      <td>{sale.seller?.full_name || 'N/A'}</td>
                      <td>{sale.amount?.formatted || formatCurrency(sale.amount?.value || 0)}</td>
                      <td className="text-center">{sale.installments || 'N/A'}</td>
                      <td className="text-center">
                        <span
                          className={`sale-status-badge ${getSaleDisplayStatusClass(sale)}`}
                        >
                          {getSaleDisplayStatusLabel(sale) || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            title="Ver detalle de la observación"
                            onClick={() => handleViewDetail(sale)}
                            aria-label="Ver observación"
                          >
                            <i className="bi bi-exclamation-circle me-1" aria-hidden></i>
                            Observación
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            title="Ver detalle de la venta"
                            onClick={() => setSaleDetailToShow(sale)}
                            aria-label="Ver detalle de la venta"
                          >
                            <i className="bi bi-eye me-1" aria-hidden></i>
                            Venta
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          }
          pagination={<Pagination loading={loading} />}
        />

        {/* En este flujo las notas de observación no se eliminan desde la UI (solo frontend). */}
        {showDetailModal && selectedSale && (
          <ObservationSaleDetailModal
            observationId={selectedSale.observation_id}
            saleId={selectedSale.id}
            customerId={selectedSale.customer?.uid}
            sale={selectedSale}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedSale(null);
            }}
          />
        )}

        {saleDetailToShow && saleDetailToShow.customer?.id != null && (
          <SaleDetailModal
            saleId={saleDetailToShow.id}
            customerId={saleDetailToShow.customer.id}
            onClose={() => setSaleDetailToShow(null)}
          />
        )}
      </section>
    </div>
  );
};

export default ObservationPage;

